import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { ChevronDown, MapPin, Search } from "lucide-react";

import { getPublicHub, type HubDTO, type HubPlaceDTO } from "@/lib/hubs.functions";
import { SmartImage } from "@/components/ui/SmartImage";
import { computeOpenStatus } from "@/lib/opening-hours";

export const hubQuery = (slug: string) =>
  queryOptions({
    queryKey: ["public-hub", slug],
    queryFn: () => getPublicHub({ data: { slug } }),
  });

type Props = { slug: string; locale: "en" | "fi" };

function attr(p: HubPlaceDTO, key: string): string | null {
  const v = p.attributes?.[key];
  return v === null || v === undefined || v === "" ? null : String(v);
}

const FOOD_KINDS = new Set(["restaurant", "cafe", "bar", "pub"]);

/** The two badges shown on a card differ per hub topic. */
function cardBadges(kind: string, p: HubPlaceDTO, locale: "en" | "fi"): string[] {
  if (FOOD_KINDS.has(kind)) {
    return [attr(p, "cuisine") ?? p.subcategory ?? p.category, attr(p, "price_range") ?? p.price].filter(
      (v): v is string => Boolean(v),
    );
  }
  if (kind === "sauna") {
    return [attr(p, "sauna_type"), attr(p, "swim")].filter((v): v is string => Boolean(v));
  }
  if (kind === "ski_resort") {
    const lifts = attr(p, "lifts");
    return [lifts ? `${lifts} ${locale === "fi" ? "hissiä" : "lifts"}` : null, attr(p, "season")].filter(
      (v): v is string => Boolean(v),
    );
  }
  const est = attr(p, "established");
  const area = attr(p, "area_km2");
  return [est ? `${locale === "fi" ? "Perustettu" : "Est."} ${est}` : null, area ? `${area} km²` : null].filter(
    (v): v is string => Boolean(v),
  );
}

export function HubIndexPage({ slug, locale }: Props) {
  const { data } = useSuspenseQuery(hubQuery(slug)) as {
    data: { hub: HubDTO; places: HubPlaceDTO[] } | null;
  };
  const [q, setQ] = useState("");
  const [region, setRegion] = useState<string>("all");

  const places = data?.places ?? [];
  const hub = data?.hub;

  const regions = useMemo(() => {
    const set = new Set<string>();
    for (const p of places) {
      const r = attr(p, "region");
      if (r) set.add(r);
    }
    return Array.from(set).sort();
  }, [places]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return places.filter((p) => {
      if (region !== "all" && attr(p, "region") !== region) return false;
      if (!needle) return true;
      return (
        p.name.toLowerCase().includes(needle) ||
        (attr(p, "municipality") ?? "").toLowerCase().includes(needle) ||
        (attr(p, "region") ?? "").toLowerCase().includes(needle) ||
        (attr(p, "cuisine") ?? p.subcategory ?? p.category ?? "").toLowerCase().includes(needle)
      );
    });
  }, [places, q, region]);

  if (!hub) return null;

  const title = locale === "fi" ? hub.name_fi || hub.name : hub.name;
  const tagline = locale === "fi" ? hub.tagline_fi || hub.tagline : hub.tagline;
  const description = locale === "fi" ? hub.description_fi || hub.description : hub.description;
  const base = locale === "fi" ? `/fi/${hub.slug}` : `/${hub.slug}`;

  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-7xl px-6 py-14 sm:px-12 sm:py-20">
        {/* Header */}
        <header className="mb-12 sm:mb-16">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                {locale === "fi" ? "Teemasivu" : "Finland-wide"} • {title}
              </p>
              <h1 className="font-heading text-5xl leading-[1.05] text-foreground md:text-7xl">
                {title}
              </h1>
              {tagline && (
                <p className="mt-4 text-lg font-medium text-muted-foreground">{tagline}</p>
              )}
              {description && (
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
                  {description}
                </p>
              )}
            </div>
            <div className="md:text-right">
              <span className="text-sm font-medium text-muted-foreground">
                {locale === "fi"
                  ? `Näytetään ${filtered.length} kohdetta`
                  : `Showing ${filtered.length} places`}
              </span>
            </div>
          </div>

          {/* Filter bar */}
          <div className="mt-10 flex flex-col gap-1 rounded-2xl border border-border bg-muted/60 p-2 md:flex-row md:items-stretch md:gap-0">
            <label className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={
                  locale === "fi" ? "Hae nimellä tai paikkakunnalla…" : "Search by name, cuisine or city…"
                }
                className="w-full bg-transparent py-4 pl-12 pr-4 text-foreground outline-none placeholder:text-muted-foreground"
                aria-label={locale === "fi" ? "Hae" : "Search"}
              />
            </label>
            {regions.length > 1 && (
              <>
                <div className="mx-2 hidden w-px bg-border md:block" />
                <div className="relative flex items-center">
                  <select
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className="w-full cursor-pointer appearance-none bg-transparent px-6 py-4 pr-10 text-sm font-medium text-foreground outline-none md:w-auto"
                    aria-label={locale === "fi" ? "Maakunta" : "Region"}
                  >
                    <option value="all">{locale === "fi" ? "Kaikki alueet" : "All regions"}</option>
                    {regions.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 h-4 w-4 text-muted-foreground" />
                </div>
              </>
            )}
          </div>
        </header>

        {/* Card grid */}
        <ul className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => {
            const badges = cardBadges(hub.kind, p, locale);
            const location = [attr(p, "municipality"), attr(p, "region")].filter(Boolean).join(" · ");
            return (
              <li key={p.id}>
                <Link
                  to={`${base}/$slug` as any}
                  params={{ slug: p.slug } as any}
                  className="group relative flex h-[420px] flex-col overflow-hidden rounded-2xl bg-muted transition-transform duration-500 hover:-translate-y-2 sm:h-[520px]"
                >
                  <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-110">
                    {p.image_url ? (
                      <SmartImage
                        src={p.image_url}
                        alt={p.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        <MapPin className="h-10 w-10" />
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent" />
                  <HubOpenPill place={p} locale={locale} />

                  <div className="relative mt-auto p-6 sm:p-8">
                    {badges.length > 0 && (
                      <div className="mb-4 flex flex-wrap gap-2">
                        {badges.map((b) => (
                          <span
                            key={b}
                            className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-md"
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    )}
                    <h2 className="font-heading mb-2 text-3xl text-white">{p.name}</h2>
                    {location && <p className="text-sm text-white/70">{location}</p>}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
        {filtered.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {places.length === 0
                ? locale === "fi"
                  ? "Tälle sivulle ei ole vielä lisätty kohteita. Oletko yrittäjä? Lähetä oma kohteesi."
                  : "No places listed here yet. Run one? Submit your own venue."
                : locale === "fi"
                  ? "Ei osumia."
                  : "No matches."}
            </p>
            {places.length === 0 && (
              <Link
                to="/partner/apply"
                className="mt-4 inline-block rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground"
              >
                {locale === "fi" ? "Lisää kohde" : "Submit a listing"}
              </Link>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

function HubOpenPill({ place, locale }: { place: HubPlaceDTO; locale: "en" | "fi" }) {
  const status = computeOpenStatus({
    opening_hours: (place.opening_hours ?? null) as never,
    special_hours: (place.special_hours ?? null) as never,
    temporarily_closed: place.temporarily_closed ?? false,
    schedule_info: place.schedule_info ?? null,
  });
  if (status.state === "unknown") return null;
  const open = status.state === "open" || status.state === "closing_soon";
  const label = open
    ? status.state === "closing_soon"
      ? locale === "fi"
        ? "Sulkeutuu pian"
        : "Closing soon"
      : locale === "fi"
        ? "Avoinna nyt"
        : "Open now"
    : locale === "fi"
      ? "Suljettu"
      : "Closed";
  return (
    <span
      className={`absolute left-5 top-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider backdrop-blur-md ${
        open ? "bg-emerald-500/90 text-white" : "bg-black/45 text-white/85"
      }`}
    >
      <span className={`size-1.5 rounded-full ${open ? "bg-white" : "bg-white/60"}`} />
      {label}
    </span>
  );
}
